import React, { useState, useEffect } from "react";
import {
  Button,
  message,
  Row,
  Col,
  Input,
  Drawer,
  Form,
  Select,
  Switch,
  Upload,
  ColorPicker,
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { bannerAPIWithErrorHandling, bannerHelpers } from "../../../api/banner/banner";
import BrandedPageLayout from "../../../components/Dashboard/BrandedPageLayout";
import ListTableCard from "../../../components/common/ListTableCard";
import { confirmDelete } from "../../../components/common/confirmDialog";
import { actionColumnDef } from "../../../components/common/RowActions";
import ListToolbar, {
  selectVisibleColumns,
  useListToolbar,
} from "../../../components/common/ListToolbar";
import { neutral, spacing } from "../../../theme/tokens";

const DEFAULT_VISIBLE_FILTERS = [];
const DEFAULT_VISIBLE_COLUMNS = ["details", "status"];

export default function BannerTable() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
  });
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [form] = Form.useForm();

  const {
    visibleFilters, setVisibleFilters,
    visibleColumns, setVisibleColumns,
  } = useListToolbar({
    defaultVisibleFilters: DEFAULT_VISIBLE_FILTERS,
    defaultVisibleColumns: DEFAULT_VISIBLE_COLUMNS,
  });

  const fetchBanners = async (page = 1, pageSize = 15, search = "", status = "") => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: pageSize,
        search: search.trim(),
        status: status,
      };
      const response = await bannerAPIWithErrorHandling.getBanners(params);
      const { banners: data, pagination: paginationData } = bannerHelpers.extractBannerList(response);

      setBanners(data);
      setPagination({
        current: paginationData.current,
        pageSize: paginationData.pageSize,
        total: paginationData.total,
      });
    } catch (error) {
      message.error(error.message || "Không thể tải danh sách banner");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTableChange = (paginationConfig) => {
    fetchBanners(paginationConfig.current, paginationConfig.pageSize, searchText, statusFilter);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    fetchBanners(1, pagination.pageSize, value, statusFilter);
  };

  const handleStatusChange = (value) => {
    setStatusFilter(value);
    fetchBanners(1, pagination.pageSize, searchText, value);
  };

  const handleReset = () => {
    setSearchText("");
    setStatusFilter("");
    fetchBanners(1, pagination.pageSize, "", "");
  };

  const handleToggleStatus = async (id) => {
    try {
      await bannerAPIWithErrorHandling.toggleStatusBanner(id);
      message.success("Cập nhật trạng thái thành công");
      fetchBanners(pagination.current, pagination.pageSize, searchText, statusFilter);
    } catch (error) {
      message.error(error.message);
    }
  };

  const handleDelete = async (record) => {
    const confirmed = await confirmDelete({ entity: "banner", name: record.title });
    if (!confirmed) return;

    try {
      await bannerAPIWithErrorHandling.deleteBanner(record.id);
      message.success("Xóa banner thành công");
      fetchBanners(pagination.current, pagination.pageSize, searchText, statusFilter);
    } catch (error) {
      message.error(error.message);
    }
  };

  const showModal = (record = null) => {
    setSelectedRecord(record);
    if (record) {
      form.setFieldsValue({
        ...record,
        bg_color: record.bg_color || "#0d6efd",
      });
      if (record.image_url) {
        setFileList([{ url: record.image_url, name: "image.png" }]);
      } else {
        setFileList([]);
      }
    } else {
      form.resetFields();
      form.setFieldsValue({ status: true, bg_color: "#0d6efd" });
      setFileList([]);
    }
    setIsModalOpen(true);
  };

  const handleModalSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const formData = new FormData();
      formData.append("title", values.title);
      formData.append("subtitle", values.subtitle || "");
      formData.append("bg_color", typeof values.bg_color === 'string' ? values.bg_color : values.bg_color?.toHexString());
      formData.append("status", values.status ? 1 : 0);

      if (fileList.length > 0 && fileList[0].originFileObj) {
        formData.append("image", fileList[0].originFileObj);
      }

      if (selectedRecord) {
        await bannerAPIWithErrorHandling.updateBanner(selectedRecord.id, formData);
        message.success("Cập nhật banner thành công");
      } else {
        await bannerAPIWithErrorHandling.storeBanner(formData);
        message.success("Thêm banner thành công");
      }

      setIsModalOpen(false);
      fetchBanners(pagination.current, pagination.pageSize, searchText, statusFilter);
    } catch (error) {
      if (error.name !== "ValidationError") {
        message.error(error.message || "Lỗi xử lý yêu cầu");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleUploadChange = ({ fileList: newFileList }) => setFileList(newFileList);

  // ===== Định nghĩa cột =====
  const columnDefs = [
    {
      key: "banner", label: "Banner", fixed: true,
      column: {
        title: "Banner",
        key: "banner",
        render: (_, record) => (
          <div
            className="rounded-lg p-4 text-white overflow-hidden relative flex flex-col justify-center min-h-[100px]"
            style={{
              backgroundColor: record.bg_color || "#eee",
              backgroundImage: record.image_url ? `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3)), url(${record.image_url})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              width: '300px'
            }}
          >
            <div className="font-bold text-lg leading-tight mb-1">{record.title}</div>
            <div className="text-xs opacity-90">{record.subtitle}</div>
          </div>
        ),
      },
    },
    {
      key: "details", label: "Chi tiết",
      column: {
        title: "Chi tiết",
        key: "details",
        render: (_, record) => (
          <div>
            <div className="text-sm font-medium">BG: <code className="bg-gray-100 p-1 rounded">{record.bg_color}</code></div>
          </div>
        ),
      },
    },
    {
      key: "status", label: "Trạng thái",
      column: {
        title: "Trạng thái",
        dataIndex: "status",
        key: "status",
        width: 100,
        render: (status, record) => (
          <Switch
            size="small"
            checked={status}
            onChange={() => handleToggleStatus(record.id)}
          />
        ),
      },
    },
    actionColumnDef(
      (record) => [
        { key: "edit", label: "Chỉnh sửa", icon: <EditOutlined />, tone: "edit", onClick: () => showModal(record) },
        { key: "delete", label: "Xóa", icon: <DeleteOutlined />, tone: "danger", onClick: () => handleDelete(record) },
      ],
      { width: 100 }
    ),
  ];

  // ===== Định nghĩa bộ lọc =====
  const filterDefs = [
    {
      key: "search", label: "Tìm kiếm", span: { xs: 24, sm: 12, md: 8 },
      field: (
        <Input.Search
          placeholder="Tìm kiếm banner..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onSearch={handleSearch}
          enterButton={<SearchOutlined />}
          allowClear
        />
      ),
    },
    {
      key: "status", label: "Trạng thái", span: { xs: 24, sm: 12, md: 8 },
      field: (
        <Select
          style={{ width: "100%" }}
          placeholder="Lọc trạng thái"
          value={statusFilter}
          onChange={handleStatusChange}
          allowClear
        >
          <Select.Option value="true">Đang hiển thị</Select.Option>
          <Select.Option value="false">Đang ẩn</Select.Option>
        </Select>
      ),
    },
  ];

  const columns = selectVisibleColumns(columnDefs, visibleColumns);
  const hasActiveFilter = !!(searchText || statusFilter);

  return (
    <BrandedPageLayout
      title="Banner"
      subtitle="Quản lý danh sách banner hiển thị trên hệ thống."
      breadcrumbItems={[{ title: "Banner" }, { title: "Danh sách" }]}
    >
      <ListToolbar
        filterDefs={filterDefs}
        visibleFilters={visibleFilters}
        onChangeVisibleFilters={setVisibleFilters}
        columnDefs={columnDefs}
        visibleColumns={visibleColumns}
        onChangeVisibleColumns={setVisibleColumns}
        defaultVisibleColumns={DEFAULT_VISIBLE_COLUMNS}
        hasActiveFilter={hasActiveFilter}
        onClearFilter={handleReset}
        onReload={() => fetchBanners(pagination.current, pagination.pageSize, searchText, statusFilter)}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal()}>
            Thêm Banner
          </Button>
        }
      />

      <ListTableCard
        columns={columns}
        dataSource={banners}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
        }}
        onChange={handleTableChange}
        emptyText="Không có dữ liệu"
      />

      <Drawer
        title={
          <div className="flex items-center gap-2">
            {selectedRecord ? <EditOutlined className="text-blue-500" /> : <PlusOutlined className="text-green-500" />}
            <span>{selectedRecord ? "Chỉnh sửa Banner" : "Thêm Banner mới"}</span>
          </div>
        }
        placement="right"
        width="min(640px, 100vw)"
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        destroyOnClose
        styles={{ body: { background: neutral[50], padding: spacing[6] } }}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: spacing[2] }}>
            <Button onClick={() => setIsModalOpen(false)} disabled={submitting}>
              Hủy
            </Button>
            <Button type="primary" loading={submitting} onClick={handleModalSubmit}>
              {selectedRecord ? "Cập nhật" : "Thêm mới"}
            </Button>
          </div>
        }
      >
        <Form form={form} layout="vertical" className="mt-4 pt-4 border-t">
          <Row gutter={24}>
            <Col span={24}>
              <Form.Item
                name="title"
                label="Tiêu đề chính"
                rules={[{ required: true, message: "Vui lòng nhập tiêu đề!" }]}
              >
                <Input placeholder="Ví dụ: Kỷ niệm các ngày lễ lớn" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="subtitle"
                label="Tiêu đề phụ"
              >
                <Input placeholder="Ví dụ: Tinh thần 30/4 & Quốc tế lao động 1/5" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="bg_color"
                label="Màu nền"
              >
                <ColorPicker showText disabledAlpha />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                label="Trạng thái"
                valuePropName="checked"
              >
                <Switch checkedChildren="Hiển thị" unCheckedChildren="Ẩn" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Hình ảnh Banner">
            <Upload
              listType="picture-card"
              fileList={fileList}
              onChange={handleUploadChange}
              beforeUpload={() => false}
              maxCount={1}
            >
              {fileList.length < 1 && (
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>Tải ảnh</div>
                </div>
              )}
            </Upload>
            <div className="text-xs text-gray-400 mt-2 italic">* Nếu tải ảnh, ảnh sẽ được hiển thị đè lên màu nền.</div>
          </Form.Item>

        </Form>
      </Drawer>
    </BrandedPageLayout>
  );
}
